import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Grid, Image, Search, Sparkles } from 'lucide-react';
import ImageSearchModal from '@/components/admin/ImageSearchModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
});

const AdminCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', image: '' });
  const [showImageSearch, setShowImageSearch] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(
          `${API}/admin/categories/${editingCategory.id}`,
          formData,
          getAuthHeaders()
        );
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${API}/admin/categories`, formData, getAuthHeaders());
        toast.success('Category created successfully');
      }
      setDialogOpen(false);
      setEditingCategory(null);
      setFormData({ name: '', image: '' });
      fetchCategories();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error saving category:', error);
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({ name: category.name, image: category.image || '' });
    setDialogOpen(true);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`, getAuthHeaders());
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const openAddDialog = () => {
    setEditingCategory(null);
    setFormData({ name: '', image: '' });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading categories...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-outfit font-bold text-slate-900">
            Categories Management
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Manage product categories for your store
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={openAddDialog}
              className="bg-[#0047AB] hover:bg-[#003380]"
              data-testid="add-category-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">Category Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Electronics"
                  required
                  data-testid="category-name-input"
                />
              </div>
              <div>
                <Label htmlFor="image">Image URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="image"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    className="flex-1"
                    data-testid="category-image-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setShowImageSearch(true)}
                    title="Search for images"
                    data-testid="search-category-image-btn"
                  >
                    <Sparkles className="w-4 h-4" />
                  </Button>
                </div>
                {formData.image && (
                  <div className="mt-2">
                    <img
                      src={formData.image}
                      alt="Preview"
                      className="w-20 h-20 object-cover rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#0047AB] hover:bg-[#003380]"
                  data-testid="save-category-btn"
                >
                  {editingCategory ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {categories.map((category) => (
          <div
            key={category.id}
            className="bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-md transition-shadow"
          >
            <div className="aspect-video bg-slate-100 relative">
              {category.image ? (
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image className="w-12 h-12 text-slate-300" />
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Grid className="w-4 h-4 text-[#0047AB]" />
                <h3 className="font-semibold text-slate-900">{category.name}</h3>
              </div>
              <p className="text-xs text-slate-400 mb-3">ID: {category.id}</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(category)}
                  className="flex-1"
                  data-testid={`edit-category-${category.id}`}
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(category.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-category-${category.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {categories.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
          <Grid className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No categories found</p>
          <Button
            onClick={openAddDialog}
            className="mt-4 bg-[#0047AB] hover:bg-[#003380]"
          >
            Add Your First Category
          </Button>
        </div>
      )}

      {/* Image Search Modal */}
      <ImageSearchModal
        open={showImageSearch}
        onOpenChange={setShowImageSearch}
        onSelect={(url) => {
          setFormData({ ...formData, image: url });
          setShowImageSearch(false);
        }}
        initialQuery={formData.name}
      />
    </div>
  );
};

export default AdminCategoriesPage;
